import PropTypes from 'prop-types';
import React, {
  useEffect,
  useState,
  useRef,
} from 'react';

import { Map, Iterable } from 'immutable';
import isEmpty           from 'lodash/isEmpty';
import WaveSurfer        from 'wavesurfer.js';

import Box        from '@mui/material/Box';
import Button     from '@mui/material/Button';
import Grid       from '@mui/material/Grid';
import grey       from '@mui/material/colors/grey';
import Typography from '@mui/material/Typography';

import Mic          from '@mui/icons-material/Mic';
import Stop         from '@mui/icons-material/Stop';
import PlayCircle   from '@mui/icons-material/PlayCircle';
import Replay       from '@mui/icons-material/Replay';
import { useTheme } from '@mui/material/styles';

import Display from 'components/Generic/Display';

import drawOscilloscope from 'helpers/record_audio';
import { isValidURL } from 'helpers/url';

function Audio({
  id = null,
  name = null,
  value = null,
  onChange = null,
  readOnly = false,
  disabled = false,
  min = 0,
  max = 120,
  rerecordable = true,
  forced = false,
}) {
  // TODO: Implement record minimum and limit for TalkScore questions.
  const LIMIT = max || 120; // 2 min
  const AUDIO_MIME_TYPES = ['audio/webm;codecs=opus', 'audio/ogg;codecs=opus', 'audio/webm'];
  const theme = useTheme();

  const [state, setState] = useState(new Map());
  const [counter, setCounter] = useState(0);

  const isRecording      = state.get('isRecording', false);
  const audiourl         = state.get('audiourl', '');
  const isPreviewPlaying = state.get('isPreviewPlaying', false);
  const recorder         = state.get('recorder', null);
  const duration         = state.get('duration', '0:00');
  const timerFinished    = state.get('timerFinished', false);

  const refWavesurfer       = useRef();
  const refCanvas           = useRef();
  const refAudioChunks      = useRef([]);
  const waveSurferObjectRef = useRef();
  const intervalRef         = useRef(null);

  useEffect(() => {
    if (recorder) {
      recorder.onstart = () => {
        intervalRef.current = setInterval(() => {
          setCounter((prev) => prev + 1);
        }, 1000);
      };
    }
  }, [recorder]);

  useEffect(() => {
    if (counter === 0) return;

    const minutes = parseInt(counter / 60, 10);
    let seconds   = parseInt(counter % 60, 10);

    seconds = seconds < 10 ? `0${seconds}` : seconds;

    setState(state.set('duration', `${minutes}:${seconds}`));

    if (counter > LIMIT) {
      onStop();
    }
  }, [counter]);

  useEffect(() => {
    if (isEmpty(audiourl)) return;

    if (isEmpty(waveSurferObjectRef.current)) {
      waveSurferObjectRef.current = WaveSurfer.create({
        container:     refWavesurfer.current,
        waveColor:     theme.palette.primary.main,
        progressColor: theme.palette.primary.dark,
        barHeight:     1,
        barWidth:      1,
        normalize:     true,
        backend:       'MediaElement',
      });
    }

    waveSurferObjectRef.current.load(audiourl);
  }, [audiourl]);

  useEffect(() => {
    if (value === null) return;

    let url = '';
    let length = '0:00';
    if (!isEmpty(value.get('blob'))) {
      url = window.URL.createObjectURL(value.get('blob')[0]);
      length = value.get('duration');
    } else if (isValidURL(value.get('url'))) {
      // This code is actually not used, only unanswered questions are shown.
      url = value.get('url');
    } else return;

    // Check if value is url and set it to the audiourl state variable.
    // waveSurferObjectRef.current.load(url);
    setState(state.set('audiourl', url).set('duration', length));
  }, [value]);

  useEffect(() => {
    if (recorder == null) return;

    // this code is working on safari as opposed to recorder.ondataavailable
    recorder.addEventListener('dataavailable', ({ data }) => {
      if (data instanceof Blob && data.size > 0) {
        refAudioChunks.current.push(data);
      }

      // We must return true to keep the recorder running.
      return true;
    });

    // Start recording
    recorder.start();
  }, [recorder]);

  useEffect(() => {
    // WHEN START
    if (isRecording && isEmpty(recorder)) {
      navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount:     1,
          sampleRate:       16000,
          sampleSize:       16,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl:  true,
        },
      }).then((mediaStream) => {
        const mimeType = AUDIO_MIME_TYPES.find((t) => MediaRecorder.isTypeSupported(t));
        const options = { audioBitsPerSecond: 32000 };
        if (mimeType) {
          options.mimeType = mimeType;
        }
        setState(state.set('recorder', new MediaRecorder(mediaStream, options)));

        drawOscilloscope(mediaStream, refCanvas.current);
        // detectVoice(mediaStream);
      });
    }
    // WHEN STOP
    // not using else for clarity
    // means the recording is complete. ready for submission
    if (!isRecording && recorder) {
      // Remove “recording” icon from browser tab
      recorder.stream.getTracks().forEach((i) => { i.stop(); });

      // eventlistener to handle the wavesurfer generation
      recorder.addEventListener('stop', () => {
        if (!isEmpty(refAudioChunks.current)) {
          const blob = new Blob(refAudioChunks.current, { type: recorder.mimeType });
          const audioURL = window.URL.createObjectURL(blob);

          setState(state.set('audiourl', audioURL));
          onChange(name, new Map({
            blob: [blob], duration, forced: timerFinished, incomplete: counter < min,
          }));
        }

        return true;
      });

      recorder.stop();
      refAudioChunks.current = [];
    }
  }, [isRecording]);

  function resetAll() {
    // recorder    = null; // will be used for the stream
    refAudioChunks.current = []; // global variable

    // empty wavesurfer container
    // refWavesurfer.current.innerHTML = '';
    waveSurferObjectRef.current.empty();

    setState(state.set('audiourl', null)
      .set('recorder', null).set('duration', '0:00'));

    onChange(name, new Map());
    setCounter(0);
  }

  function onStart() {
    setState(state.set('isRecording', true));
  }

  function onStop() {
    setState(state.set('isRecording', false));
    clearInterval(intervalRef.current);
  }

  function playPreview() {
    if (isEmpty(waveSurferObjectRef.current)) return;

    if (!isPreviewPlaying) {
      waveSurferObjectRef.current.play(0);
    } else {
      waveSurferObjectRef.current.pause();
    }

    setState(state.set('isPreviewPlaying', !isPreviewPlaying));
  }

  useEffect(() => {
    if (!forced) return;

    if (isRecording) {
      setState(state.set('timerFinished', true)); // Mid recording
      return;
    }

    if (value && value.get('blob')) { // Answer present
      onChange(name, new Map({ forced: true }));
      return;
    }

    // Candidate didn't record once
    onChange(name, new Map({
      blob: null, duration: 0, text: t('eos.landing_page.label.no_answer'), forced: true, incomplete: true,
    }));
  }, [forced]);

  useEffect(() => {
    if (timerFinished && isRecording) {
      onStop();
    }
  }, [timerFinished, isRecording]);

  // To Do: Separate recorder and display of the audio so we can use the input read only.

  return (
    <Grid item xs={12} sx={{ mt: 2 }}>
      <Box sx={{
        display: 'flex', flexDirection: 'column', minHeight: 100, border: 1, borderRadius: 1, justifyContent: 'center', alignItems: 'center', borderColor: grey[400], p: 2,
      }}
      >
        <Box ref={refWavesurfer} data-testid="wavesurfer" sx={{ width: '90%', borderColor: grey[500] }} />

        <Display if={!isEmpty(audiourl)}>
          <Typography data-testid="generic-audio-timer" variant="p">{duration}</Typography>
        </Display>

        <Display id="display-visualizer" if={isRecording}>
          <Box sx={{ width: '90%', borderColor: grey[500] }}>
            <canvas ref={refCanvas} data-testid="canvas" style={{ width: '100%', height: 100 }} />
          </Box>

          <Typography data-testid="generic-audio-timer" variant="p">{duration}</Typography>

          <Button data-testid="stop_recording" variant="contained" color="primary" onClick={() => onStop()} startIcon={<Stop />} sx={{ mt: 2 }} disabled={counter < min}>{t('eos.landing_page.label.stop_recording')}</Button>
        </Display>
        <Display if={!isRecording}>
          <Display id="display-player" if={!isEmpty(audiourl)}>
            <Grid
              container
              item
              spacing={1}
              xs={12}
              sx={{
                mt:             2,
                display:        'flex',
                justifyContent: 'center',
                button:         {
                  width: '100%',
                },
              }}
            >
              <Grid
                item
                md={3}
                xs={12}
                sx={{
                  display:        'flex',
                  justifyContent: 'flex-end',
                }}
              >
                <Button data-testid="play_recording" variant="outlined" color={isPreviewPlaying ? 'error' : 'primary'} onClick={() => playPreview()} startIcon={isPreviewPlaying ? <Stop /> : <PlayCircle />} disabled={disabled}>{isPreviewPlaying ? t('eos.landing_page.label.stop') : t('eos.landing_page.label.play')}</Button>
              </Grid>
              <Display if={rerecordable}>
                <Grid
                  item
                  md={3}
                  xs={12}
                  sx={{
                    display:        'flex',
                    justifyContent: 'flex-start',
                  }}
                >
                  <Button data-testid="new_recording" variant="outlined" color="primary" onClick={() => resetAll()} startIcon={<Replay />} disabled={disabled}>{t('eos.landing_page.label.new_recording')}</Button>
                </Grid>
              </Display>
            </Grid>
          </Display>
          <Display id="display-player" if={!recorder && isEmpty(audiourl)}>
            <Button data-testid="record_now" variant="contained" color="primary" onClick={() => onStart()} startIcon={<Mic />} sx={{ display: 'flex' }} disabled={disabled}>{t('eos.landing_page.label.record_now')}</Button>
          </Display>
        </Display>
      </Box>
    </Grid>
  );
}

Audio.propTypes = {
  id:           PropTypes.string,
  name:         PropTypes.string,
  value:        PropTypes.objectOf(Iterable),
  onChange:     PropTypes.func,
  readOnly:     PropTypes.bool,
  disabled:     PropTypes.bool,
  min:          PropTypes.number,
  max:          PropTypes.number,
  rerecordable: PropTypes.bool,
  forced:       PropTypes.bool,
};

export default Audio;
